/**
 * SpinalMilestoneBloom — a wordless golden ceremony that fires once
 * per milestone (7/14/30/50/100-day streaks, first session, family-first
 * etc.) on app open. Never repeats: acknowledgement is persisted server-side.
 */

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SpinalMilestoneBloom = ({ milestone, onClose }) => {
  // Auto-dismiss after 12 seconds — this is meant to breathe, not to
  // demand action. She can also tap anywhere to dismiss sooner.
  useEffect(() => {
    if (!milestone) return;
    const t = setTimeout(() => onClose?.(), 12000);
    return () => clearTimeout(t);
  }, [milestone, onClose]);

  // Fire acknowledgement so this milestone never triggers again.
  useEffect(() => {
    if (!milestone?.code) return;
    fetch(`${API}/api/healing/milestones/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ code: milestone.code }),
    }).catch(() => { /* non-fatal; check-endpoint will retry next open */ });
  }, [milestone]);

  if (!milestone) return null;

  const hue = milestone.hue || 'rgba(230,190,130,0.9)';

  return (
    <AnimatePresence>
      <motion.div
        key="milestone-bloom"
        className="fixed inset-0 z-[95] flex items-center justify-center px-6 cursor-pointer"
        style={{
          background: 'radial-gradient(circle at 50% 45%, rgba(20,14,8,0.75) 0%, rgba(4,3,2,0.94) 65%)',
          backdropFilter: 'blur(14px)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.3 }}
        onClick={onClose}
        data-testid="milestone-bloom"
      >
        {/* Outer golden bloom — swells slowly and never fully leaves */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 640,
            height: 640,
            background: `radial-gradient(circle, ${hue.replace(/0\.\d+\)$/, '0.35)')} 0%, ${hue.replace(/0\.\d+\)$/, '0.12)')} 40%, transparent 75%)`,
            filter: 'blur(30px)',
          }}
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: [0.4, 1.05, 1], opacity: [0, 0.95, 0.75] }}
          transition={{ duration: 4.5, times: [0, 0.55, 1], ease: 'easeOut' }}
        />
        {/* Inner sun — bright core */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 120,
            height: 120,
            background: `radial-gradient(circle, rgba(255,250,225,1) 0%, ${hue} 45%, transparent 85%)`,
            boxShadow: `0 0 120px ${hue}, 0 0 240px ${hue.replace(/0\.\d+\)$/, '0.4)')}`,
          }}
          initial={{ scale: 0.2, opacity: 0 }}
          animate={{ scale: [0.2, 1.15, 1], opacity: [0, 1, 0.9] }}
          transition={{ duration: 3.2, times: [0, 0.6, 1], ease: 'easeOut' }}
        />
        {/* 8 radial rays — appear after the core has bloomed */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
          <motion.div
            key={deg}
            className="absolute origin-center pointer-events-none"
            style={{
              width: 340,
              height: 1.5,
              background: `linear-gradient(90deg, transparent 0%, ${hue} 50%, transparent 100%)`,
              transform: `rotate(${deg}deg)`,
              filter: 'blur(0.6px)',
            }}
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: [0, 0.85, 0.55], scaleX: [0, 1.05, 1] }}
            transition={{ duration: 3.6, delay: 1.2 + i * 0.08, ease: 'easeOut' }}
          />
        ))}

        {/* Message — arrives softly, no button, no confetti */}
        <motion.div
          className="relative z-10 text-center max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 1.6 }}
        >
          <p
            className="text-white/40 text-[10px] tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {milestone.kind === 'streak' ? 'A quiet ceremony' :
              milestone.kind === 'family' ? 'A moment of holding' :
                milestone.kind === 'sessions' ? 'A gathered practice' :
                  'A first return'}
          </p>
          <h2
            className="text-white/95 text-3xl leading-tight mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 300 }}
            data-testid="milestone-title"
          >
            {milestone.title}
          </h2>
          <p
            className="text-white/60 text-[14px] leading-relaxed"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            data-testid="milestone-message"
          >
            {milestone.message}
          </p>
          <motion.p
            className="text-white/25 text-[10px] mt-8"
            style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.25em' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 5.5, duration: 1.2 }}
          >
            tap anywhere to continue
          </motion.p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SpinalMilestoneBloom;
